import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { useKodiakFarmEarned } from 'hooks/useKodiakFarm'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { useOptionalRewards } from 'pages/Islands/IslandPage/context/RewardContext'
import { useMemo } from 'react'
import { KodiakFarm } from 'types/farms'
import { ApiFarm } from 'types/portfolio'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { Address } from 'viem'

type FarmRewardsSource = KodiakFarm | (ApiFarm & { address?: string })

interface FarmRewardsDisplayProps {
  farm: FarmRewardsSource
  className?: string
  emptyClassName?: string
}

export function FarmRewardsDisplay({ farm, className = '', emptyClassName = '' }: FarmRewardsDisplayProps) {
  const rewardsContext = useOptionalRewards()
  const farmAddress = useMemo(() => {
    const rawAddress = (farm as { address?: string }).address || farm.id
    return rawAddress?.startsWith('0x') ? (rawAddress as Address) : undefined
  }, [farm])

  const farmForRewards = useMemo(() => {
    if (!farmAddress) return undefined
    return { ...(farm as object), address: farmAddress } as KodiakFarm
  }, [farm, farmAddress])

  const fallbackEarned = useKodiakFarmEarned(
    rewardsContext ? undefined : farmForRewards,
    undefined,
    PORTFOLIO_REFETCH_POLICY
  )
  const contextEarned = useMemo(
    () =>
      rewardsContext?.kodiakFarmEarned
        ?.map((item) => item.amount)
        .filter((amount): amount is NonNullable<typeof amount> => Boolean(amount)) ?? null,
    [rewardsContext?.kodiakFarmEarned]
  )
  const earned = contextEarned ?? fallbackEarned
  const rewardTokenAddresses = useMemo(
    () => earned?.filter((amount) => Boolean(amount?.currency?.address)).map((amount) => amount.currency.address) || [],
    [earned]
  )
  const { prices } = useGetTokenPrices(rewardTokenAddresses)

  const rewards = useMemo(() => {
    if (!earned) return []
    return earned
      .map((amount, index) => {
        if (!amount || !amount.currency || amount.equalTo('0')) return null
        const exact = parseFloat(amount.toExact())
        const price = prices?.[index] ?? 0
        const usd = exact * (price || 0)
        return {
          symbol: amount.currency.symbol,
          amountText: formatNumber(exact) || '0',
          usdText: formatDollarAmount(usd),
        }
      })
      .filter((item): item is { symbol: string; amountText: string; usdText: string } => item !== null)
  }, [earned, prices])

  if (!rewards.length) {
    return <div className={`text-kodiak-text-secondary text-right text-xs ${emptyClassName}`}>—</div>
  }

  return (
    <div className={`text-[#6EE7B7] space-y-1 ${className}`}>
      {rewards.map((reward) => (
        <div key={reward.symbol}>
          <span>
            {reward.amountText} {reward.symbol}
          </span>{' '}
          <span className="text-[#6EE7B7]">({reward.usdText})</span>
        </div>
      ))}
    </div>
  )
}
